import type {
  PolicyReportContextPayload,
  SafetyCaseRecord,
  SafetyDashboardRow,
} from '../../services/reportsApi';
import { resolveHazardClassOption } from '../../pages/acceptanceProof/hazardClassReportOptions';
import { formatRiskPatternSummary } from '../incidents/riskPatternWorkflowReviewContext';
import { AFTER_SIGNED_SCAN_REVIEW_SCOPE } from '../incidents/afterSignedScanReview';

const CLOSED_STATUSES = new Set(['closed', 'archived']);

function normalize(value: unknown): string {
  return String(value || '').trim().toLowerCase();
}

function dateTimestamp(value?: string | null): number {
  const ms = new Date(String(value || '').trim()).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function readScope(item: SafetyCaseRecord): Record<string, unknown> {
  return (item.scope && typeof item.scope === 'object') ? item.scope as Record<string, unknown> : {};
}

function readPolicyContext(
  item: (SafetyCaseRecord | SafetyDashboardRow) | null | undefined,
): PolicyReportContextPayload | null {
  const scope = readScope((item || {}) as SafetyCaseRecord);
  const topLevel = (item && typeof item === 'object') ? item as Record<string, unknown> : {};
  const context = topLevel.policy_report_context || topLevel.report_context || scope.policy_report_context || scope.report_context;
  return (context && typeof context === 'object') ? context as PolicyReportContextPayload : null;
}

function humanizeToken(value: unknown): string {
  return String(value || '')
    .trim()
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function displayCameraLabel(value: unknown): string {
  const camera = String(value || '').trim();
  if (camera.toLowerCase() === 'cam_walkway_zone_demo') return 'Warehouse Room B – Walkway';
  return camera;
}

function readSnapshot(item: SafetyCaseRecord): Record<string, unknown> {
  return (item.status_snapshot && typeof item.status_snapshot === 'object')
    ? item.status_snapshot as Record<string, unknown>
    : {};
}

function readMatchKeys(item: Pick<SafetyCaseRecord, 'safety_case_id' | 'finding_id' | 'workflow_id'>): string[] {
  return [item.safety_case_id, item.finding_id, item.workflow_id]
    .map((value) => String(value || '').trim())
    .filter(Boolean);
}

function indexDashboardRows(rows: SafetyDashboardRow[]): Map<string, SafetyDashboardRow[]> {
  const index = new Map<string, SafetyDashboardRow[]>();
  rows.forEach((row) => {
    if (normalize(row.report_type) === 'category') return;
    readMatchKeys(row).forEach((key) => {
      const bucket = index.get(key) || [];
      if (!bucket.includes(row)) bucket.push(row);
      index.set(key, bucket);
    });
  });
  return index;
}

function lookupDashboardRows(index: Map<string, SafetyDashboardRow[]>, item: SafetyCaseRecord): SafetyDashboardRow[] {
  const matches = new Map<SafetyDashboardRow, true>();
  readMatchKeys(item).forEach((key) => {
    (index.get(key) || []).forEach((row) => matches.set(row, true));
  });
  return Array.from(matches.keys());
}

function pickPrimaryDashboardRow(rows: SafetyDashboardRow[]): SafetyDashboardRow | null {
  if (!rows.length) return null;
  return [...rows].sort((left, right) => {
    const leftScore = Math.max(
      dateTimestamp(left.latest_observation_at || left.source_workflow_updated_at || left.updated_at),
      dateTimestamp(left.report_finalized_at || left.latest_scan_signed_at || left.report_created_at),
    );
    const rightScore = Math.max(
      dateTimestamp(right.latest_observation_at || right.source_workflow_updated_at || right.updated_at),
      dateTimestamp(right.report_finalized_at || right.latest_scan_signed_at || right.report_created_at),
    );
    return rightScore - leftScore;
  })[0];
}

export function enrichSafetyCasesWithDashboardRows(
  cases: SafetyCaseRecord[],
  dashboardRows: SafetyDashboardRow[],
): SafetyCaseRecord[] {
  const index = indexDashboardRows(dashboardRows);
  return cases.map((caseItem) => {
    const primaryRow = pickPrimaryDashboardRow(lookupDashboardRows(index, caseItem));
    if (!primaryRow) return caseItem;

    const scope = { ...readScope(caseItem) };
    if (!scope.camera_id && primaryRow.camera_id) scope.camera_id = primaryRow.camera_id;
    if (!scope.zone_id && primaryRow.zone_id) scope.zone_id = primaryRow.zone_id;
    if (!scope.area_label && primaryRow.area_label) scope.area_label = primaryRow.area_label;
    if (!scope.risk_type && primaryRow.risk_type) scope.risk_type = primaryRow.risk_type;
    if (!scope.risk_label && primaryRow.risk_label) scope.risk_label = primaryRow.risk_label;
    if (!scope.policy_report_context && primaryRow.policy_report_context) {
      scope.policy_report_context = primaryRow.policy_report_context;
    }
    if (!scope.report_context && primaryRow.policy_report_context) {
      scope.report_context = primaryRow.policy_report_context;
    }

    return {
      ...caseItem,
      scope,
      risk_type: primaryRow.risk_type || caseItem.risk_type || undefined,
      risk_label: primaryRow.risk_label || caseItem.risk_label || undefined,
      risk_pattern_label: primaryRow.risk_pattern_label || caseItem.risk_pattern_label || undefined,
      camera_id: primaryRow.camera_id || caseItem.camera_id || undefined,
      camera_label: primaryRow.camera_label || caseItem.camera_label || undefined,
      zone_id: primaryRow.zone_id || caseItem.zone_id || undefined,
      zone_label: primaryRow.zone_label || caseItem.zone_label || undefined,
      area_label: primaryRow.area_label || caseItem.area_label || undefined,
      device_id: primaryRow.device_id || caseItem.device_id || undefined,
      device_label: primaryRow.device_label || caseItem.device_label || undefined,
      report_finalized_at: primaryRow.report_finalized_at || primaryRow.latest_scan_signed_at || caseItem.report_finalized_at || undefined,
      latest_scan_signed_at: primaryRow.latest_scan_signed_at || caseItem.latest_scan_signed_at || undefined,
      latest_observation_at: primaryRow.latest_observation_at || caseItem.latest_observation_at || undefined,
      source_workflow_updated_at: primaryRow.source_workflow_updated_at || caseItem.source_workflow_updated_at || undefined,
      has_new_activity_since_report: primaryRow.has_new_activity_since_report ?? caseItem.has_new_activity_since_report,
      followup_required: primaryRow.followup_required ?? caseItem.followup_required,
      workflow_changed_since_signoff: primaryRow.workflow_changed_since_signoff ?? caseItem.workflow_changed_since_signoff,
      latest_finalized_report_id: caseItem.latest_finalized_report_id || primaryRow.latest_finalized_report_id || undefined,
      active_draft_report_id: caseItem.active_draft_report_id || primaryRow.active_draft_report_id || undefined,
    };
  });
}

export function safetyCaseDedupeKey(item: SafetyCaseRecord): string {
  return String(item.safety_case_id || item.finding_id || item.workflow_id || '').trim();
}

export function dedupeSafetyCases(rows: SafetyCaseRecord[]): SafetyCaseRecord[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const key = safetyCaseDedupeKey(row);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function isManagedSafetyCase(item: SafetyCaseRecord): boolean {
  if (normalize(item.status) === 'archived') return false;
  return !CLOSED_STATUSES.has(normalize(item.safety_case_status));
}

export function readRiskType(item: SafetyCaseRecord): string {
  const scope = readScope(item);
  return String(item.risk_type || scope.risk_type || item.hazard_category || item.finding_type || '').trim();
}

export function readRiskLabel(item: SafetyCaseRecord): string {
  const scope = readScope(item);
  return String(item.risk_label || scope.risk_label || '').trim();
}

export function originatingRiskPatternLabel(item: SafetyCaseRecord): string {
  const context = readPolicyContext(item);
  if (String(context?.hazard_exposure_label || '').trim()) {
    return String(context?.hazard_exposure_label || '').trim();
  }
  return formatRiskPatternSummary({
    risk_type: readRiskType(item),
    risk_label: readRiskLabel(item),
    risk_pattern_label: item.risk_pattern_label || undefined,
  });
}

export function hazardCategoryLabel(item: SafetyCaseRecord): string {
  const context = readPolicyContext(item);
  if (String(context?.exposure_category_label || '').trim()) {
    return String(context?.exposure_category_label || '').trim();
  }
  if (String(context?.exposure_category || '').trim()) {
    return humanizeToken(context?.exposure_category);
  }
  const key = String(item.hazard_category || readRiskType(item) || '').trim();
  const option = resolveHazardClassOption(key);
  if (option?.displayName) return option.displayName;
  return key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Not recorded';
}

export function readSiteLabel(item: SafetyCaseRecord, fallbackSiteId?: string | null): string {
  return String(item.site_id || readScope(item).site_id || fallbackSiteId || '').trim() || 'Not recorded';
}

export function readZoneLabel(item: SafetyCaseRecord): string {
  const scope = readScope(item);
  const anchor = (item.workflow_anchor && typeof item.workflow_anchor === 'object')
    ? item.workflow_anchor as Record<string, unknown>
    : {};
  const zones = Array.isArray(scope.zones) ? scope.zones : [];
  const zone = String(
    item.zone_label
    || item.area_label
    || item.zone_id
    || scope.zone_id
    || zones[0]
    || anchor.zone_id
    || '',
  ).trim();
  if (zone) return zone;
  return 'Not recorded';
}

export function readAreaLabel(item: SafetyCaseRecord): string | null {
  const scope = readScope(item);
  const area = String(item.area_label || scope.area_label || scope.area || '').trim();
  return area || null;
}

export function readCameraLabel(item: SafetyCaseRecord): string | null {
  const scope = readScope(item);
  const anchor = (item.workflow_anchor && typeof item.workflow_anchor === 'object')
    ? item.workflow_anchor as Record<string, unknown>
    : {};
  const camera = String(
    item.camera_label
    || item.camera_id
    || scope.camera_id
    || anchor.camera_id
    || '',
  ).trim();
  return displayCameraLabel(camera) || null;
}

export function readDeviceLabel(item: SafetyCaseRecord): string | null {
  const scope = readScope(item);
  const anchor = (item.workflow_anchor && typeof item.workflow_anchor === 'object')
    ? item.workflow_anchor as Record<string, unknown>
    : {};
  const device = String(
    item.device_label
    || item.device_id
    || scope.device_id
    || anchor.device_id
    || '',
  ).trim();
  return device || null;
}

function isMalformedWorkflowId(workflowId: string): boolean {
  if (!workflowId) return true;
  if (workflowId.includes('|-|')) return true;
  if (workflowId.includes('|') && workflowId.split('|').filter(Boolean).length >= 2) return true;
  return false;
}

/** Short customer-facing workflow id, or null when internal/malformed. */
export function formatCustomerWorkflowLabel(workflowId?: string | null): string | null {
  const raw = String(workflowId || '').trim();
  if (!raw || isMalformedWorkflowId(raw)) return null;
  if (raw.length <= 16) return raw;
  return raw.slice(0, 16);
}

function titleCaseWords(value: string): string {
  return String(value || '').replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function portfolioCaseStateLabel(row: {
  safety_case_status_label?: string | null;
  safety_case_status?: string | null;
  finding_status?: string | null;
}): string {
  const explicit = String(row.safety_case_status_label || row.safety_case_status || '').trim();
  if (explicit) return titleCaseWords(explicit);
  if (normalize(String(row.finding_status || '')) === 'closed') return 'Closed';
  return 'Active';
}

export function portfolioReviewStateLabel(row: {
  review_state?: string | null;
  latest_review_status_label?: string | null;
  latest_review_status?: string | null;
  review_status?: string | { status?: string | null; status_label?: string | null } | null;
}): string | null {
  const nested = row.review_status && typeof row.review_status === 'object'
    ? row.review_status
    : null;
  const raw = String(
    row.review_state
    || row.latest_review_status_label
    || row.latest_review_status
    || nested?.status_label
    || nested?.status
    || (typeof row.review_status === 'string' ? row.review_status : '')
    || '',
  ).trim();
  if (!raw) return null;
  const normalized = normalize(raw);
  if (normalized === 'pending') return 'Under review';
  if (normalized === 'reviewed') return 'Reviewed';
  return titleCaseWords(raw);
}

export function portfolioOutcomeStateLabel(row: {
  safety_case_status?: string | null;
  safety_case_status_label?: string | null;
}): string | null {
  const status = normalize(String(row.safety_case_status || ''));
  if (status === 'validated') return 'Validated';
  const label = normalize(String(row.safety_case_status_label || ''));
  if (label === 'validated') return 'Validated';
  return null;
}

export function safetyCaseReviewStateLabel(item: SafetyCaseRecord): string | null {
  return portfolioReviewStateLabel(item);
}

export function safetyCaseOutcomeStateLabel(item: SafetyCaseRecord): string | null {
  return portfolioOutcomeStateLabel(item);
}

export function safetyCaseDisambiguatorLabel(item: SafetyCaseRecord): string | null {
  const camera = readCameraLabel(item);
  if (camera) return `Camera ${camera}`;
  const device = readDeviceLabel(item);
  if (device) return `Device ${device}`;
  const zone = readZoneLabel(item);
  if (zone !== 'Not recorded') return `Zone ${zone}`;
  const workflowLabel = formatCustomerWorkflowLabel(item.workflow_id);
  if (workflowLabel) return `Workflow ${workflowLabel}`;
  return null;
}

export function safetyCaseCardTitle(item: SafetyCaseRecord): string {
  const riskLabel = originatingRiskPatternLabel(item);
  const disambiguator = safetyCaseDisambiguatorLabel(item);
  return disambiguator
    ? `Safety Case: ${riskLabel} - ${disambiguator}`
    : `Safety Case: ${riskLabel}`;
}

export function currentCaseStateLabel(item: SafetyCaseRecord): string {
  return portfolioCaseStateLabel(item);
}

export function readSignedScanAt(item: SafetyCaseRecord): string | null {
  const snapshot = readSnapshot(item);
  return String(
    item.report_finalized_at
    || item.latest_scan_signed_at
    || snapshot.report_finalized_at
    || snapshot.latest_signed_scan_at
    || snapshot.latest_finalized_report_at
    || '',
  ).trim() || null;
}

export function readLatestObservationAt(item: SafetyCaseRecord): string | null {
  const snapshot = readSnapshot(item);
  return String(
    item.latest_observation_at
    || snapshot.latest_activity_at
    || item.source_workflow_updated_at
    || item.updated_at
    || '',
  ).trim() || null;
}

export function latestSignedSafetyScanLabel(item: SafetyCaseRecord): string {
  if (!item.latest_finalized_report_id) return 'None';
  const signedAt = readSignedScanAt(item);
  if (signedAt) {
    const parsed = new Date(signedAt);
    if (!Number.isNaN(parsed.getTime())) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      }).format(parsed);
    }
    return signedAt;
  }
  return 'Signed scan on file';
}

export function scanHistoryCountLabel(item: SafetyCaseRecord): string {
  const linked = Number(item.linked_report_count || 0);
  return `${linked} linked scan${linked === 1 ? '' : 's'}`;
}

export function hasPendingRecommendationDecision(item: SafetyCaseRecord): boolean {
  return (item.recommendations || []).some(
    (recommendation) => !recommendation.current_decision_state && !recommendation.latest_decision?.decision_state,
  );
}

export function caseNeedsReview(item: SafetyCaseRecord): boolean {
  const status = normalize(item.safety_case_status);
  if (['open', 'action_ready'].includes(status)) return true;
  if (hasPendingRecommendationDecision(item)) return true;
  if (item.recommendations?.length === 0 && ['open', 'action_assigned'].includes(status)) return true;
  return false;
}

export function hasNewActivitySinceSignedScan(item: SafetyCaseRecord): boolean {
  if (!item.latest_finalized_report_id) return false;

  if (item.followup_required || item.has_new_activity_since_report || item.workflow_changed_since_signoff) {
    return true;
  }

  const snapshot = readSnapshot(item);
  if (snapshot.has_new_activity_since_report === true || snapshot.followup_required === true) {
    return true;
  }

  const signedAt = readSignedScanAt(item);
  const latestObservation = readLatestObservationAt(item);
  if (!signedAt || !latestObservation) return false;
  return dateTimestamp(latestObservation) > dateTimestamp(signedAt);
}

export function newActivityStateLabel(item: SafetyCaseRecord): string {
  if (!item.latest_finalized_report_id) return 'Not applicable';
  return hasNewActivitySinceSignedScan(item)
    ? 'New activity since latest Signed Safety Scan: Yes'
    : 'New activity since latest Signed Safety Scan: No';
}

export function nextCaseStepLabel(item: SafetyCaseRecord): string {
  const status = normalize(item.safety_case_status);
  const recommendations = item.recommendations || [];

  if (status === 'open' || status === 'action_ready') {
    if (recommendations.length === 0) return 'Review the case and generate recommendations.';
    if (hasPendingRecommendationDecision(item)) return 'Review recommendation decisions before packaging a scan.';
    if (item.active_draft_report_id) return 'Continue the draft Safety Scan when ready for sign-off.';
    return 'Decide whether to create or update a Safety Scan.';
  }
  if (['action_assigned', 'action_completed', 'followup_pending', 'measurement_review'].includes(status)) {
    return 'Complete corrective-action follow-up and measurement review.';
  }
  if (status === 'validated') {
    if (hasNewActivitySinceSignedScan(item)) {
      return 'Review new activity and create an updated Safety Scan only if it needs to be shared.';
    }
    return 'Review validated outcomes or create an updated scan when follow-up changes.';
  }
  return 'Open the Safety Case to review the next step.';
}

export function countValidatedOutcomes(items: SafetyCaseRecord[]): number {
  return items.reduce(
    (total, item) => total + Number(item.outcome_learning_summary?.validated_count || 0),
    0,
  );
}

export function buildSafetyCaseReviewNewActivityPath(
  item: SafetyCaseRecord,
  scope: { siteId: string; orgId?: string | null },
): string {
  const query = new URLSearchParams();
  query.set('site_id', String(scope.siteId || readSiteLabel(item)).trim());
  query.set('tab', 'open');
  query.set('table_mode', 'grouped');
  query.set('review', '1');
  if (item.workflow_id) query.set('workflow_id', String(item.workflow_id));
  if (item.representative_incident_id) query.set('incident_id', String(item.representative_incident_id));
  const zone = readZoneLabel(item);
  if (zone !== 'Not recorded') query.set('zone_id', zone);
  const camera = readCameraLabel(item);
  if (camera) query.set('camera_id', camera);
  const device = readDeviceLabel(item);
  if (device) query.set('device_id', device);
  const riskType = readRiskType(item);
  if (riskType) query.set('policy_type', riskType);
  const caseId = String(item.safety_case_id || item.finding_id || '').trim();
  if (caseId) query.set('case_id', caseId);
  query.set('review_scope', AFTER_SIGNED_SCAN_REVIEW_SCOPE);
  const signedAt = readSignedScanAt(item);
  if (signedAt) query.set('signed_scan_at', signedAt);
  const latestObservation = readLatestObservationAt(item);
  if (latestObservation) query.set('new_activity_latest_observation', latestObservation);
  const reviewState = String(item.review_status?.status_label || item.review_status?.status || '').trim();
  if (reviewState) query.set('new_activity_review_state', reviewState);
  return `/incidents?${query.toString()}`;
}

export function buildSafetyCaseWorklistSummary(items: SafetyCaseRecord[]) {
  const managedCases = dedupeSafetyCases(items.filter(isManagedSafetyCase));
  return {
    managedCases,
    totalCount: managedCases.length,
    needingReviewCount: managedCases.filter(caseNeedsReview).length,
    newActivityCount: managedCases.filter(hasNewActivitySinceSignedScan).length,
    validatedOutcomesCount: countValidatedOutcomes(managedCases),
  };
}

export function formatSafetyCaseActionOwnerLabel(
  item: Pick<SafetyCaseRecord, 'followup_owner' | 'followup_team' | 'has_followup_owner'>,
): string {
  if (item.has_followup_owner === false) return 'Not assigned';
  const owner = String(item.followup_owner || '').trim();
  const team = String(item.followup_team || '').trim();
  if (!owner && !team) return 'Not assigned';
  if (team && owner && team.toLowerCase() !== owner.toLowerCase()) return `${team} / ${owner}`;
  return owner || team;
}

export function hasCanonicalFollowupOwner(
  item: Pick<SafetyCaseRecord, 'followup_owner' | 'followup_team' | 'has_followup_owner'>,
): boolean {
  if (item.has_followup_owner === false) return false;
  return formatSafetyCaseActionOwnerLabel(item) !== 'Not assigned';
}
